import apiService from '../api'

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export type StockDispatchStatus = 'in_progress' | 'in_transit' | 'completed' | 'rerouted'

export interface WarehouseApi {
  id: string
  name: string
  location?: string | null
}

export interface StockDispatchDetailApi {
  id: string
  stockDispatchId: string
  bags?: number | null
  tonnage?: string | null
  lotNumber?: string | null
  phPi?: string | null
  contractNumber?: string | null
  bookingNumber?: string | null
  product?: string | null
  forwarder?: string | null
  container?: string | null
  receivedDate?: string | null
  freeStorageDays?: number | null
  storageStartDate?: string | null
  dispatchedDate?: string | null
  destination?: string | null
  transporter?: string | null
  truckRate?: string | null
  truckReg?: string | null
  invoiceNumber?: string | null
  chargeableDays?: number | null
  daysInStock?: number | null
  createdAt: string
  updatedAt: string
}

export interface StockDispatchApi {
  id: string
  warehouseId?: string | null
  warehouse?: WarehouseApi | null
  orderFulfillmentId?: string | null
  orderFulfillment?: Record<string, any> | null
  shipmentId?: string | null
  shipmentRef?: Record<string, any> | null
  product?: string | null
  lotNumber?: string | null
  qtyDispatched?: string | null
  qtyUnit?: string | null
  customer?: string | null
  destination?: string | null
  dispatchDate?: string | null
  status: StockDispatchStatus
  dispatchCosts?: string | null
  dispatchCostsCurrency?: string | null
  dispatchInstructionUrl?: string | null
  proofOfDeliveryUrl?: string | null
  transporterInvoiceUrl?: string | null
  salesInvoiceUrl?: string | null
  dispatchNoteImageUrl?: string | null
  flyteTripId?: string | null
  flyteTripRef?: string | null
  flyteStatus?: string | null
  flyteLastEventAt?: string | null
  notes?: string | null
  details?: StockDispatchDetailApi[]
  createdAt: string
  updatedAt: string
}

export interface DispatchNoteFile {
  id: string
  name: string
  uploadedAt: string
  mimeType?: string | null
  size?: number | null
}

export interface DocumentFileEntry {
  id: string
  name: string
  uploadedAt: string
  mimeType?: string | null
  size?: number | null
  isPrimary?: boolean
}

const PRIMARY_URL_FIELDS: Record<DocumentType, keyof StockDispatchApi> = {
  dispatchInstruction: 'dispatchInstructionUrl',
  proofOfDelivery: 'proofOfDeliveryUrl',
  transporterInvoice: 'transporterInvoiceUrl',
  salesInvoice: 'salesInvoiceUrl',
  dispatchNote: 'dispatchNoteImageUrl',
}

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  dispatchInstruction: 'Dispatch Instruction',
  proofOfDelivery: 'Proof of Delivery',
  transporterInvoice: 'Transporter Invoice',
  salesInvoice: 'Sales Invoice',
  dispatchNote: 'Dispatch Note',
}

export function getDocumentTypeLabel(type: DocumentType): string {
  return DOCUMENT_TYPE_LABELS[type]
}

type ParsedNotesPayload = {
  text: string | null
  files: DispatchNoteFile[]
  documentExtras: Partial<Record<DocumentType, DispatchNoteFile[]>>
}

function parseNotesPayload(notes?: string | null): ParsedNotesPayload {
  if (!notes) {
    return { text: null, files: [], documentExtras: {} }
  }
  try {
    const parsed = JSON.parse(notes)
    if (!parsed || typeof parsed !== 'object') {
      return { text: notes, files: [], documentExtras: {} }
    }
    const files = Array.isArray(parsed.files)
      ? parsed.files
          .filter((f: any) => f && typeof f === 'object' && f.id && f.name)
          .map((f: any) => ({
            id: String(f.id),
            name: String(f.name),
            uploadedAt: String(f.uploadedAt || ''),
            mimeType: f.mimeType ? String(f.mimeType) : null,
            size: typeof f.size === 'number' ? f.size : null,
          }))
      : []
    const documentExtras: Partial<Record<DocumentType, DispatchNoteFile[]>> = {}
    if (parsed.documentExtras && typeof parsed.documentExtras === 'object') {
      for (const type of Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]) {
        const list = parsed.documentExtras[type]
        if (!Array.isArray(list)) continue
        documentExtras[type] = list
          .filter((f: any) => f && typeof f === 'object' && f.id && f.name)
          .map((f: any) => ({
            id: String(f.id),
            name: String(f.name),
            uploadedAt: String(f.uploadedAt || ''),
            mimeType: f.mimeType ? String(f.mimeType) : null,
            size: typeof f.size === 'number' ? f.size : null,
          }))
      }
    }
    const text = typeof parsed.text === 'string' ? parsed.text : null
    return { text, files, documentExtras }
  } catch {
    return { text: notes, files: [], documentExtras: {} }
  }
}

function getExtraFilesFromDispatch(dispatch: StockDispatchApi, type: DocumentType): DispatchNoteFile[] {
  const payload = parseNotesPayload(dispatch.notes)
  if (type === 'dispatchNote') return payload.files
  return payload.documentExtras[type] ?? []
}

export function countDocumentFiles(
  dispatch: StockDispatchApi,
  type: DocumentType
): number {
  const hasPrimary = !!dispatch[PRIMARY_URL_FIELDS[type]]
  return (hasPrimary ? 1 : 0) + getExtraFilesFromDispatch(dispatch, type).length
}

export function hasDocumentUploaded(
  dispatch: StockDispatchApi,
  type: DocumentType
): boolean {
  return countDocumentFiles(dispatch, type) > 0
}

export function getDocumentButtonLabel(
  dispatch: StockDispatchApi,
  type: DocumentType
): string {
  const count = countDocumentFiles(dispatch, type)
  if (count === 0) return 'Upload'
  if (count === 1) return 'Uploaded'
  return `${count} files`
}

export function listDocumentFilesFromDispatch(
  dispatch: StockDispatchApi,
  type: DocumentType
): DocumentFileEntry[] {
  const files: DocumentFileEntry[] = []
  if (dispatch[PRIMARY_URL_FIELDS[type]]) {
    files.push({
      id: 'primary',
      name: 'Primary file',
      uploadedAt: dispatch.updatedAt,
      isPrimary: true,
    })
  }
  for (const file of getExtraFilesFromDispatch(dispatch, type)) {
    files.push({ ...file, isPrimary: false })
  }
  return files
}

export interface StockDispatchListResponse {
  stockDispatches: StockDispatchApi[]
  meta: PaginationMeta
}

export interface PendingUploadItem {
  id: string
  text: string
  dispatchId: string
  dispatchRef: string
  type: DocumentType
}

export interface PendingUploadsResponse {
  items: PendingUploadItem[]
  totalPending: number
  dispatchesAffected: number
}

export interface FlyteTripSyncResult {
  status?: 'idle' | 'running' | 'completed' | 'failed'
  fetched: number
  processed?: number
  inserted: number
  updated: number
  linkedToOrders: number
  unlinked: number
  documentsSaved: number
  errors: number
  since: string
  percent?: number
  errorMessage?: string
}

export interface GetStockDispatchesParams {
  page?: number
  limit?: number
  status?: StockDispatchStatus
  customer?: string
  warehouseId?: string
  search?: string
}

export interface CreateStockDispatchPayload {
  orderFulfillmentId?: string
  shipmentId?: string
  warehouseId?: string
  product?: string
  lotNumber?: string
  qtyDispatched?: string | number
  qtyUnit?: string
  customer?: string
  destination?: string
  dispatchDate?: string
  status?: StockDispatchStatus
  dispatchCosts?: string | number
  dispatchCostsCurrency?: string
  notes?: string
}

export interface CreateStockDispatchDetailPayload {
  bags?: number
  tonnage?: string | number
  lotNumber?: string
  phPi?: string
  contractNumber?: string
  bookingNumber?: string
  product?: string
  forwarder?: string
  container?: string
  receivedDate?: string
  freeStorageDays?: number
  storageStartDate?: string
  dispatchedDate?: string
  destination?: string
  transporter?: string
  truckRate?: string | number
  truckReg?: string
  invoiceNumber?: string
  chargeableDays?: number
  daysInStock?: number
}

export type DocumentType =
  | 'dispatchInstruction'
  | 'proofOfDelivery'
  | 'transporterInvoice'
  | 'salesInvoice'
  | 'dispatchNote'

/** Let the browser set multipart boundary (do not force Content-Type). */
const multipartUploadConfig = {
  transformRequest: [
    (data: unknown, headers: Record<string, string>) => {
      if (data instanceof FormData) {
        delete headers['Content-Type']
      }
      return data
    },
  ],
}

// ============== Stock Dispatch CRUD ==============

export async function getStockDispatches(
  params?: GetStockDispatchesParams
): Promise<StockDispatchListResponse> {
  const queryParams: Record<string, string> = {}
  if (params) {
    if (params.page) queryParams.page = String(params.page)
    if (params.limit) queryParams.limit = String(params.limit)
    if (params.status) queryParams.status = params.status
    if (params.customer) queryParams.customer = params.customer
    if (params.warehouseId) queryParams.warehouseId = params.warehouseId
    if (params.search) queryParams.search = params.search
  }
  const qs = Object.keys(queryParams).length
    ? new URLSearchParams(queryParams).toString()
    : ''
  const res = await apiService.get<{ success: boolean; data: StockDispatchListResponse }>(
    `/stock-dispatches${qs ? '?' + qs : ''}`
  )
  const data = (res as any)?.data ?? res
  return {
    stockDispatches: data.stockDispatches ?? [],
    meta: data.meta ?? { page: 1, limit: params?.limit ?? 10, total: 0, totalPages: 0 },
  }
}

export async function getPendingUploadsSummary(
  params?: GetStockDispatchesParams
): Promise<PendingUploadsResponse> {
  const queryParams: Record<string, string> = {}
  if (params) {
    if (params.status) queryParams.status = params.status
    if (params.customer) queryParams.customer = params.customer
    if (params.warehouseId) queryParams.warehouseId = params.warehouseId
    if (params.search) queryParams.search = params.search
  }
  const qs = Object.keys(queryParams).length
    ? new URLSearchParams(queryParams).toString()
    : ''
  const res = await apiService.get<{ success: boolean; data: PendingUploadsResponse }>(
    `/stock-dispatches/pending-uploads${qs ? '?' + qs : ''}`
  )
  const data = (res as any)?.data ?? res
  return {
    items: data.items ?? [],
    totalPending: data.totalPending ?? 0,
    dispatchesAffected: data.dispatchesAffected ?? 0,
  }
}

export async function getStockDispatchById(id: string): Promise<StockDispatchApi> {
  const res = await apiService.get<{ success: boolean; data: StockDispatchApi }>(
    `/stock-dispatches/${id}`
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function createStockDispatch(
  payload: CreateStockDispatchPayload
): Promise<StockDispatchApi> {
  const res = await apiService.post<{ success: boolean; data: StockDispatchApi }>(
    '/stock-dispatches',
    payload
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function updateStockDispatch(
  id: string,
  payload: Partial<CreateStockDispatchPayload>
): Promise<StockDispatchApi> {
  const res = await apiService.put<{ success: boolean; data: StockDispatchApi }>(
    `/stock-dispatches/${id}`,
    payload
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function deleteStockDispatch(id: string): Promise<void> {
  await apiService.delete(`/stock-dispatches/${id}`)
}

export async function sendStockDispatchToFlyte(
  stockDispatchId: string
): Promise<StockDispatchApi> {
  const res = await apiService.post<{ success: boolean; data: StockDispatchApi }>(
    `/stock-dispatches/${stockDispatchId}/flyte/send`
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function syncFlyteTrips(
  since = '2026-04-01'
): Promise<FlyteTripSyncResult> {
  const res = await apiService.post<{ success: boolean; data: FlyteTripSyncResult }>(
    '/stock-dispatches/import/flyte',
    { since },
    { timeout: 30000 }
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function getFlyteSyncStatus(): Promise<FlyteTripSyncResult> {
  const res = await apiService.get<{ success: boolean; data: FlyteTripSyncResult }>(
    '/stock-dispatches/import/flyte/status',
    { suppressGlobalError: true }
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function updateStockDispatchStatus(
  id: string,
  status: StockDispatchStatus
): Promise<StockDispatchApi> {
  const res = await apiService.patch<{ success: boolean; data: StockDispatchApi }>(
    `/stock-dispatches/${id}/status`,
    { status }
  )
  const data = (res as any)?.data ?? res
  return data
}

// ============== Document Upload/Download ==============

export async function uploadDocument(
  stockDispatchId: string,
  documentType: DocumentType,
  file: File
): Promise<StockDispatchApi> {
  return uploadDocuments(stockDispatchId, documentType, [file])
}

export async function uploadDocuments(
  stockDispatchId: string,
  documentType: DocumentType,
  files: File[]
): Promise<StockDispatchApi> {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))
  if (files.length === 1) {
    formData.append('file', files[0])
  }
  const res = await apiService.instance.post<{ success?: boolean; data?: StockDispatchApi }>(
    `/stock-dispatches/${stockDispatchId}/documents/${documentType}`,
    formData,
    multipartUploadConfig
  )
  const body = (res as any)?.data
  const payload = body?.data ?? body
  return payload
}

export async function getDocumentUrl(
  stockDispatchId: string,
  documentType: DocumentType
): Promise<string> {
  const res = await apiService.get<{ success: boolean; data: { url: string } }>(
    `/stock-dispatches/${stockDispatchId}/documents/${documentType}/url`
  )
  const data = (res as any)?.data ?? res
  return data?.url ?? ''
}

export async function getDocumentFiles(
  stockDispatchId: string,
  documentType: DocumentType
): Promise<DocumentFileEntry[]> {
  const res = await apiService.get<{ success: boolean; data: { files: DocumentFileEntry[] } }>(
    `/stock-dispatches/${stockDispatchId}/documents/${documentType}/files`
  )
  const data = (res as any)?.data ?? res
  return data?.files ?? []
}

export async function getDocumentFileUrl(
  stockDispatchId: string,
  documentType: DocumentType,
  fileId: string
): Promise<string> {
  const res = await apiService.get<{ success: boolean; data: { url: string } }>(
    `/stock-dispatches/${stockDispatchId}/documents/${documentType}/files/${fileId}/url`
  )
  const data = (res as any)?.data ?? res
  return resolveDispatchFileUrl(data?.url ?? '')
}

/** Local Flyte/manual files are stored as /uploads/... on the API host, not the Vite origin. */
export function resolveDispatchFileUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('/uploads/')) {
    const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
    return `${apiBase.replace(/\/api\/?$/, '')}${trimmed}`
  }
  return trimmed
}

export async function deleteDocumentFile(
  stockDispatchId: string,
  documentType: DocumentType,
  fileId: string
): Promise<StockDispatchApi> {
  const res = await apiService.delete<{ success: boolean; data: StockDispatchApi }>(
    `/stock-dispatches/${stockDispatchId}/documents/${documentType}/files/${fileId}`
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function deleteDocument(
  stockDispatchId: string,
  documentType: DocumentType
): Promise<StockDispatchApi> {
  const res = await apiService.delete<{ success: boolean; data: StockDispatchApi }>(
    `/stock-dispatches/${stockDispatchId}/documents/${documentType}`
  )
  const data = (res as any)?.data ?? res
  return data
}

/** Detail rows with a truck registration number (for the Truck Registration table). */
export function getTruckRegistrationEntries(
  details?: StockDispatchDetailApi[]
): StockDispatchDetailApi[] {
  if (!details?.length) return []
  return details.filter((d) => !!d.truckReg?.trim())
}

export function hasDispatchNoteUploaded(dispatch: StockDispatchApi): boolean {
  return hasDocumentUploaded(dispatch, 'dispatchNote')
}

export function extractDispatchNoteFiles(notes?: string | null): DispatchNoteFile[] {
  return parseNotesPayload(notes).files
}

export async function uploadDispatchNoteFiles(
  stockDispatchId: string,
  files: File[] | FileList
): Promise<StockDispatchApi> {
  const formData = new FormData()
  const list = Array.isArray(files) ? files : Array.from(files)
  list.forEach((file) => formData.append('files', file))

  const res = await apiService.instance.post<{ success?: boolean; data?: StockDispatchApi }>(
    `/stock-dispatches/${stockDispatchId}/dispatch-notes`,
    formData,
    multipartUploadConfig
  )
  const body = (res as any)?.data
  return body?.data ?? body
}

export async function getDispatchNoteFileUrl(
  stockDispatchId: string,
  fileId: string
): Promise<string> {
  return getDocumentFileUrl(stockDispatchId, 'dispatchNote', fileId)
}

export async function deleteDispatchNoteFile(
  stockDispatchId: string,
  fileId: string
): Promise<StockDispatchApi> {
  const res = await apiService.delete<{ success: boolean; data: StockDispatchApi }>(
    `/stock-dispatches/${stockDispatchId}/dispatch-notes/${fileId}`
  )
  const data = (res as any)?.data ?? res
  return data
}

// ============== Detail Records ==============

export async function addDetail(
  stockDispatchId: string,
  payload: CreateStockDispatchDetailPayload
): Promise<StockDispatchDetailApi> {
  const res = await apiService.post<{ success: boolean; data: StockDispatchDetailApi }>(
    `/stock-dispatches/${stockDispatchId}/details`,
    payload
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function updateDetail(
  stockDispatchId: string,
  detailId: string,
  payload: Partial<CreateStockDispatchDetailPayload>
): Promise<StockDispatchDetailApi> {
  const res = await apiService.put<{ success: boolean; data: StockDispatchDetailApi }>(
    `/stock-dispatches/${stockDispatchId}/details/${detailId}`,
    payload
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function deleteDetail(
  stockDispatchId: string,
  detailId: string
): Promise<void> {
  await apiService.delete(`/stock-dispatches/${stockDispatchId}/details/${detailId}`)
}

// ============== Warehouses ==============

export async function getWarehouses(): Promise<WarehouseApi[]> {
  const res = await apiService.get<{ success: boolean; data: { warehouses: WarehouseApi[] } }>(
    '/stock-dispatches/warehouses'
  )
  const data = (res as any)?.data ?? res
  return data.warehouses ?? []
}

// ============== Populate from Order Fulfillment ==============

export async function populateFromOrder(
  orderFulfillmentId: string
): Promise<Record<string, any>> {
  const res = await apiService.get<{ success: boolean; data: Record<string, any> }>(
    `/stock-dispatches/populate-from-order?orderFulfillmentId=${encodeURIComponent(orderFulfillmentId)}`
  )
  const data = (res as any)?.data ?? res
  return data
}

// ============== Google Sheets Import ==============

export interface ImportFromGoogleParams {
  sheetUrl: string
  range?: string
}

export interface ImportResult {
  inserted: number
  updated: number
  skipped: number
  errors: number
  errorDetails?: Array<{ row: number; error: string }>
}

export interface VerifySheetResult {
  rowCount: number
  headers: string[]
  previewRows?: Record<string, unknown>[]
}

export async function importFromGoogleSheets(
  params: ImportFromGoogleParams
): Promise<ImportResult> {
  const res = await apiService.post<{ success: boolean; data: ImportResult }>(
    '/stock-dispatches/import/google',
    params,
    { timeout: 120000 }
  )
  const data = (res as any)?.data ?? res
  return {
    inserted: data.inserted ?? 0,
    updated: data.updated ?? 0,
    skipped: data.skipped ?? 0,
    errors: data.errors ?? 0,
    errorDetails: data.errorDetails,
  }
}

export async function verifyGoogleSheetAccess(
  params: ImportFromGoogleParams
): Promise<VerifySheetResult> {
  const res = await apiService.post<{ success: boolean; data: VerifySheetResult }>(
    '/stock-dispatches/import/google/verify',
    params,
    { timeout: 60000 }
  )
  const data = (res as any)?.data ?? res
  return {
    rowCount: data.rowCount ?? 0,
    headers: data.headers ?? [],
    previewRows: data.previewRows,
  }
}

// ============== Helper Functions ==============

/** Convert API status to display label */
export function getStatusLabel(status: StockDispatchStatus): string {
  switch (status) {
    case 'in_progress':
      return 'In Progress'
    case 'in_transit':
      return 'In Transit'
    case 'completed':
      return 'Completed'
    case 'rerouted':
      return 'Rerouted'
    default:
      return status
  }
}

/** Convert display status to API status */
export function parseStatusFromDisplay(displayStatus: string): StockDispatchStatus {
  const s = displayStatus.toLowerCase().replace(/\s+/g, '_')
  if (s === 'in_progress' || s === 'in progress') return 'in_progress'
  if (s === 'in_transit' || s === 'in transit') return 'in_transit'
  if (s === 'completed') return 'completed'
  if (s === 'rerouted') return 'rerouted'
  return 'in_progress'
}

/** Get status color for UI */
export function getStatusColor(status: StockDispatchStatus): { textColor: string; bgColor: string } {
  switch (status) {
    case 'completed':
      return { textColor: '#13AB5B', bgColor: '#E8F7EF' }
    case 'in_transit':
      return { textColor: '#3B82F6', bgColor: '#E0E7FF' }
    case 'rerouted':
      return { textColor: '#E93323', bgColor: '#FEE2E2' }
    case 'in_progress':
      return { textColor: '#EEC233', bgColor: '#FEF9EB' }
    default:
      return { textColor: '#69697A', bgColor: '#F2F4F2' }
  }
}

/** Format quantity with unit */
export function formatQty(qty: string | null | undefined, unit: string | null | undefined): string {
  if (!qty) return '—'
  const val = parseFloat(qty.replace(/,/g, ''))
  if (isNaN(val)) return qty
  return `${val.toLocaleString()} ${unit || 'MT'}`
}

/** Format currency */
export function formatCurrency(
  amount: string | null | undefined,
  currency: string | null | undefined
): string {
  if (!amount) return '—'
  const val = parseFloat(amount.replace(/,/g, ''))
  if (isNaN(val)) return amount
  return `${currency || 'USD'} ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Format date for display */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
